import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";
import { FaTrash, FaPencilAlt, FaRegFileAlt } from "react-icons/fa";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import ListPageLayout from "../common/ListPageLayout";
import TruncatedText from "../common/TruncatedText";

const NAME_MAX_CHARS = 100;

const SurveyList = ({ surveys, handleDelete }) => {
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // ✅ Sorting
  const [sortKey, setSortKey] = useState("name"); // name | pages | questions | language
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

  const sortedSurveys = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;

    const valueFor = (s) => {
      switch (sortKey) {
        case "pages":
          return Number(s.pages_count ?? 0);
        case "questions":
          return Number(s.questions_count ?? 0);
        case "language":
          return normalize(s.language);
        case "name":
        default:
          return normalize(s.name);
      }
    };

    return [...(surveys ?? [])].sort((a, b) => {
      const av = valueFor(a);
      const bv = valueFor(b);

      // numeric sorts
      if (typeof av === "number" && typeof bv === "number") {
        if (av !== bv) return (av - bv) * dir;
      } else {
        const cmp = av.localeCompare(bv);
        if (cmp !== 0) return cmp * dir;
      }

      // stable-ish fallback
      return normalize(a.name).localeCompare(normalize(b.name));
    });
  }, [surveys, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIndicator = ({ active }) => {
    if (!active) return null;
    return sortDir === "asc" ? (
      <TriangleUpIcon ml={2} boxSize="10px" />
    ) : (
      <TriangleDownIcon ml={2} boxSize="10px" />
    );
  };

  const renderSurveyCard = (survey) => (
    <Box
      key={survey.id}
      p={4}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      shadow="sm"
      w="100%"
      bg={cardBg}
      mb={4}
      _hover={{ shadow: "md" }}
    >
      <VStack align="start" spacing={3} w="100%">
        <Box minW={0} w="100%">
          <TruncatedText
            value={survey.name}
            maxChars={NAME_MAX_CHARS}
            fontWeight="bold"
            fontSize="md"
          />
        </Box>

        <HStack spacing={2} flexWrap="wrap">
          <Badge colorScheme="blue" variant="subtle" borderRadius="md">
            Pages: {survey.pages_count ?? 0}
          </Badge>
          <Badge colorScheme="purple" variant="subtle" borderRadius="md">
            Questions: {survey.questions_count ?? 0}
          </Badge>
          <Badge colorScheme="green" variant="subtle" borderRadius="md">
            Lang: {survey.language || "—"}
          </Badge>
        </HStack>

        <HStack w="100%" justify="flex-end" spacing={2} flexWrap="wrap">
          <Button
            leftIcon={<FaRegFileAlt />}
            colorScheme="cyan"
            variant="outline"
            size="sm"
            onClick={() => navigate(`/app/surveys/${survey.id}/pages`)}
          >
            Pages
          </Button>

          <Button
            as={Link}
            to={`/app/surveys/edit/${survey.id}`}
            leftIcon={<FaPencilAlt />}
            colorScheme="yellow"
            variant="outline"
            size="sm"
          >
            Edit
          </Button>

          <Button
            leftIcon={<FaTrash />}
            colorScheme="red"
            variant="outline"
            size="sm"
            onClick={() => handleDelete(survey.id)}
          >
            Delete
          </Button>
        </HStack>
      </VStack>
    </Box>
  );

  const renderSurveyTable = () => (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      shadow="sm"
      overflow="hidden"
      mt={2}
    >
      <TableContainer w="100%" maxW="100%" minW={0} overflowX="auto">
        <Table
          variant="unstyled"
          size="md"
          tableLayout="fixed"
          w="100%"
          maxW="100%"
          sx={{
            "th, td": { borderBottom: "1px solid", borderColor: "gray.100" },
            "tbody tr:hover": { background: "gray.50" },
          }}
        >
          <colgroup>
            <col style={{ width: "46%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>

          <Thead>
            <Tr
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: cardBg,
              }}
            >
              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("name")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Name <SortIndicator active={sortKey === "name"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("pages")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
                isNumeric
              >
                Pages <SortIndicator active={sortKey === "pages"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("questions")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
                isNumeric
              >
                Questions <SortIndicator active={sortKey === "questions"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("language")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Language <SortIndicator active={sortKey === "language"} />
              </Th>

              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Actions
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {sortedSurveys.map((survey) => (
              <Tr key={survey.id}>
                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText
                      value={survey.name}
                      maxChars={NAME_MAX_CHARS}
                      fontWeight="semibold"
                    />
                  </Box>
                </Td>

                <Td py={3} isNumeric fontVariantNumeric="tabular-nums">
                  {survey.pages_count ?? 0}
                </Td>

                <Td py={3} isNumeric fontVariantNumeric="tabular-nums">
                  {survey.questions_count ?? 0}
                </Td>

                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText value={survey.language || "—"} tooltip={false} />
                  </Box>
                </Td>

                <Td py={3}>
                  <HStack spacing={2}>
                    <Tooltip label="Manage Pages" hasArrow>
                      <IconButton
                        icon={<FaRegFileAlt />}
                        aria-label="Manage Pages"
                        title="Manage Pages"
                        variant="outline"
                        colorScheme="cyan"
                        size="sm"
                        onClick={() => navigate(`/app/surveys/${survey.id}/pages`)}
                      />
                    </Tooltip>

                    <Tooltip label="Edit" hasArrow>
                      <IconButton
                        as={Link}
                        to={`/app/surveys/edit/${survey.id}`}
                        icon={<FaPencilAlt />}
                        aria-label="Edit Survey"
                        title="Edit Survey"
                        variant="outline"
                        size="sm"
                        colorScheme="yellow"
                      />
                    </Tooltip>

                    <Tooltip label="Delete" hasArrow>
                      <IconButton
                        icon={<FaTrash />}
                        aria-label="Delete Survey"
                        title="Delete Survey"
                        variant="outline"
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDelete(survey.id)}
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  const actions = (
    <HStack spacing={3} flexWrap="wrap">
      {isMobile && (
        <Select
          size="sm"
          w="220px"
          value={`${sortKey}:${sortDir}`}
          onChange={(e) => {
            const [k, d] = e.target.value.split(":");
            setSortKey(k);
            setSortDir(d);
          }}
        >
          <option value="name:asc">Name (A–Z)</option>
          <option value="name:desc">Name (Z–A)</option>
          <option value="pages:asc">Pages (Low–High)</option>
          <option value="pages:desc">Pages (High–Low)</option>
          <option value="questions:asc">Questions (Low–High)</option>
          <option value="questions:desc">Questions (High–Low)</option>
          <option value="language:asc">Language (A–Z)</option>
          <option value="language:desc">Language (Z–A)</option>
        </Select>
      )}

      <Button
        as={Link}
        to="/app/surveys/create"
        colorScheme="green"
        variant="outline"
        size="sm"
      >
        Add Survey
      </Button>
    </HStack>
  );

  return (
    <ListPageLayout title="Surveys" actions={actions} contentProps={{ maxW: "100%", minW: 0 }}>
      {sortedSurveys.length > 0 ? (
        isMobile ? (
          <Box mt={2}>{sortedSurveys.map(renderSurveyCard)}</Box>
        ) : (
          renderSurveyTable()
        )
      ) : (
        <Text mt={10} textAlign="center">
          No surveys found.
        </Text>
      )}
    </ListPageLayout>
  );
};

export default SurveyList;